import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Exercise } from "../../../types";
import exercicies from "../../../services/exercicies";

const PAGE_SIZE = 20;

export function useClassificacao() {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [deletingExercise, setDeletingExercise] = useState<Exercise | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const loadExercises = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await exercicies.list_all_paginated("all_view", {
        page,
        per_page: PAGE_SIZE,
        busca: debouncedSearch,
        only_mine: false,
        classification: "no",
      });

      if (result.error) {
        setError(result.error);
        toast.error(result.error);
      }

      setExercises(result.data ?? []);
      setHasNext(result.hasNext);
      setTotalCount(result.totalCount);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    void loadExercises();
  }, [loadExercises, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingExercise(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditingExercise(null);
    refresh();
  }, [refresh]);

  const requestDelete = useCallback((exercise: Exercise) => {
    setDeletingExercise(exercise);
  }, []);

  const cancelDelete = useCallback(() => {
    if (!deleteLoading) {
      setDeletingExercise(null);
    }
  }, [deleteLoading]);

  const confirmDelete = useCallback(async () => {
    if (!deletingExercise?.id) return;

    setDeleteLoading(true);
    const result = await exercicies.deleteItem(String(deletingExercise.id));
    setDeleteLoading(false);

    if (!result.ok) {
      toast.error(result.error ?? "Erro ao excluir o exercício.");
      return;
    }

    toast.success("Exercício excluído com sucesso.");
    setDeletingExercise(null);
    refresh();
  }, [deletingExercise, refresh]);

  return {
    exercises,
    loading,
    error,
    search,
    page,
    pageSize: PAGE_SIZE,
    hasNext,
    totalCount,
    editingExercise,
    deletingExercise,
    deleteLoading,
    setPage,
    setEditingExercise,
    handleSearchChange,
    closeEditModal,
    handleEditSuccess,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
}

